<template>
  <yt-chart-card title="Détail des vidéos" subtitle="Toutes les vidéos vues sur la période. Cliquez sur un en-tête pour trier, sur une ligne pour ouvrir la fiche." :sources="['yt', 'gsc', 'calc']">
    <template #tools>
      <rmr-text-field :value="search" placeholder="Rechercher un titre, un site ou un identifiant…" :prepend-inner-icon="icons.mdiMagnify" clearable class="yt-search" @input="onSearch" />
      <span class="yt-count">{{ fInt(total) }} vidéo(s)</span>
    </template>
    <v-data-table
      :headers="headers"
      :items="videos"
      :options.sync="tableOptions"
      :server-items-length="total"
      :loading="loading"
      :footer-props="{ 'items-per-page-options': [15, 30, 50, 100], 'items-per-page-text': 'Lignes par page' }"
      class="yt-data-table"
      dense
      @click:row="(row) => $emit('open-video', row.video_id)"
    >
      <template #item.title="{ item }">
        <div class="yt-cell-video">
          <img v-if="item.thumbnail_url" :src="item.thumbnail_url" alt="" loading="lazy" />
          <div>
            <div class="yt-cell-video__t">{{ item.title }}</div>
            <div class="yt-cell-video__m">{{ item.site_name }} · <code>{{ item.video_id }}</code></div>
          </div>
        </div>
      </template>
      <template #item.published_at="{ item }">{{ fDayLong(item.published_at) }}</template>
      <template #item.duration_s="{ item }">{{ fDuration(item.duration_s) }}</template>
      <template #item.views="{ item }">{{ fInt(item.views) }}</template>
      <template #item.revenue="{ item }">{{ fEur(item.revenue) }}</template>
      <template #item.rpm="{ item }">{{ fEur2(item.rpm) }}</template>
      <template #item.avg_duration="{ item }">{{ fDuration(item.avg_duration) }}</template>
      <template #item.avg_percentage="{ item }">{{ fPct100(item.avg_percentage) }}</template>
      <template #item.likes="{ item }">{{ fInt(item.likes) }}</template>
      <template #item.dislikes="{ item }">{{ fInt(item.dislikes) }}</template>
      <template #item.comments="{ item }">{{ fInt(item.comments) }}</template>
      <template #item.shares="{ item }">{{ fInt(item.shares) }}</template>
      <template #item.discover_views="{ item }">{{ fInt(item.discover_views) }}</template>
      <template #item.discover_impressions="{ item }">{{ fInt(item.discover_impressions) }}</template>
      <template #item.discover_ctr="{ item }">{{ fPct(item.discover_ctr, 2) }}</template>
    </v-data-table>
  </yt-chart-card>
</template>

<script>
import { mdiMagnify } from '@mdi/js'
import { ref, toRefs, watch } from '@vue/composition-api'
import debounce from 'lodash/debounce'
import RmrTextField from '@/components/ui/forms/RmrTextField.vue'
import YtChartCard from '../YtChartCard.vue'
import useYoutubePerf from '../useYoutubePerf'
import { fDayLong, fDuration, fEur, fEur2, fInt, fPct, fPct100 } from '../ytFormat'

const HEADERS = [
  { text: 'Vidéo', value: 'title' },
  { text: 'Publication', value: 'published_at', align: 'end' },
  { text: 'Durée', value: 'duration_s', align: 'end' },
  { text: 'Vues', value: 'views', align: 'end' },
  { text: 'Revenus', value: 'revenue', align: 'end' },
  { text: 'RPM', value: 'rpm', align: 'end' },
  { text: 'Durée moy.', value: 'avg_duration', align: 'end' },
  { text: '% vu', value: 'avg_percentage', align: 'end' },
  { text: 'Likes', value: 'likes', align: 'end' },
  { text: 'Dislikes', value: 'dislikes', align: 'end' },
  { text: 'Comm.', value: 'comments', align: 'end' },
  { text: 'Partages', value: 'shares', align: 'end' },
  { text: 'Vues Discover', value: 'discover_views', align: 'end' },
  { text: 'Impr. Discover', value: 'discover_impressions', align: 'end', sortable: false },
  { text: 'CTR Discover', value: 'discover_ctr', align: 'end', sortable: false },
]

export default {
  name: 'YtDetailTab',
  components: { RmrTextField, YtChartCard },
  props: { active: { type: Boolean, default: false } },
  setup(props) {
    const { active } = toRefs(props)
    const { get, useTabLoader } = useYoutubePerf()
    const videos = ref([])
    const total = ref(0)
    const loading = ref(false)
    const search = ref('')
    const tableOptions = ref({ page: 1, itemsPerPage: 15, sortBy: ['views'], sortDesc: [true] })

    const fetchVideos = async () => {
      loading.value = true
      try {
        const options = tableOptions.value
        const { data } = await get('/videos', {
          sort_by: options.sortBy?.[0] || 'views',
          sort_desc: options.sortDesc?.[0] !== false,
          page: options.page || 1,
          limit: options.itemsPerPage || 15,
          ...(search.value ? { search: search.value } : {}),
        })
        videos.value = data.items || []
        total.value = data.total || 0
      } finally {
        loading.value = false
      }
    }
    useTabLoader(active, () => {
      tableOptions.value = { ...tableOptions.value, page: 1 }
      return fetchVideos()
    })
    watch(tableOptions, () => { if (active.value) fetchVideos() }, { deep: true })
    const onSearch = debounce((value) => {
      search.value = value || ''
      tableOptions.value = { ...tableOptions.value, page: 1 }
    }, 350)

    return { headers: HEADERS, videos, total, loading, search, tableOptions, onSearch, icons: { mdiMagnify }, fDayLong, fDuration, fEur, fEur2, fInt, fPct, fPct100 }
  },
}
</script>

<style lang="scss" scoped>
.yt-search { min-width: 280px; }
.yt-count { font-size: $rmr-fs-sm; color: $rmr-text-subtle; white-space: nowrap; }
</style>
